// HTML seguro da Torre. Em outubro de 2026, nomes de processos e filas vindos da UiPath e do Power Automate viraram código
// no painel porque o HTML era montado concatenando texto. Daqui em diante, todo HTML montado a partir de dado passa por
// html``, que escapa tudo o que entra, e só vai para a página por poeHtml(). A política CSP do servidor é a segunda barreira.

const ESCAPES: Readonly<Record<string, string>> = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/** Texto pronto para entrar em HTML, inclusive dentro de atributo (entre aspas duplas ou simples). */
export function esc(valor: unknown): string {
  return String(valor ?? "").replace(/[&<>"']/g, (c) => ESCAPES[c] ?? c);
}

/** Um trecho de HTML já seguro. Só nasce de html``, de bruto() (constante escrita no código) ou da junção de outros. */
export class Html {
  readonly texto: string;

  constructor(texto: string) {
    this.texto = texto;
  }

  toString(): string {
    return this.texto;
  }
}

/** O que pode entrar num html``: texto e números são escapados; Html entra como está; listas são juntadas; vazios somem. */
export type Valor = string | number | boolean | null | undefined | Html | readonly Valor[];

function junta(v: Valor): string {
  if (v instanceof Html) return v.texto;
  if (Array.isArray(v)) return (v as readonly Valor[]).map(junta).join("");
  if (v === null || v === undefined || v === false) return "";
  return esc(v);
}

/** html`<b>${nome}</b>`: nome é escapado; um html`` dentro de outro entra como está. */
export function html(partes: TemplateStringsArray, ...valores: Valor[]): Html {
  let s = partes[0] ?? "";
  for (let i = 0; i < valores.length; i++) s += junta(valores[i]) + (partes[i + 1] ?? "");
  return new Html(s);
}

/** HTML de confiança, escrito no próprio código. Nunca com dado vindo da plataforma, do servidor ou da pessoa. */
export function bruto(texto: string): Html {
  return new Html(texto);
}

/** O único caminho para pôr HTML na página: innerHTML só recebe o que saiu de html`` ou bruto(). */
export function poeHtml(alvo: Element, h: Html): void {
  alvo.innerHTML = h.texto;
}

/** Endereço vindo de dado para href ou src: http(s), mailto e caminhos relativos passam; javascript:, data: e outros
 * esquemas viram "#", mesmo disfarçados com tabulação, quebra de linha ou caracteres de controle (o navegador os ignora). */
export function urlSegura(u: unknown): string {
  // eslint-disable-next-line no-control-regex
  const t = String(u ?? "").replace(/[\t\n\r]/g, "").replace(/^[\u0000- ]+|[\u0000- ]+$/g, "");
  if (/^(https?:|mailto:)/i.test(t)) return t;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t)) return "#";
  if (/^[\\/]{2}|^\/[\\/]/.test(t)) return "#";  // //site e /\site: o navegador os lê como outra origem
  return t;
}

/** Para onde voltar depois do login (?volta=): só um caminho desta mesma Torre. Nada de //site, /\site (o navegador lê \
 * como /), outro esquema, segmentos . e .. que viram //site depois de resolvidos (/a/..//site), nem as telas de entrar e
 * da Microsoft. Devolve null quando não serve. O servidor tem o gêmeo em Python (destino_seguro, em servidor.py). */
export function destinoSeguro(volta: string | null | undefined, origem: string): string | null {
  const v = volta ?? "";
  if (!/^\/(?![/\\])[^\s\\]*$/.test(v)) return null;
  let u: URL;
  try {
    u = new URL(v, origem);
  } catch {
    return null;
  }
  if (u.origin !== new URL(origem).origin || u.pathname.startsWith("//") || /^\/(login|trocar-senha|auth(\/|$))/.test(u.pathname)) return null;
  return u.pathname + u.search + u.hash;
}
